import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import DateInput from "./DateInput";
import "./RechercheModal.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const EMPTY_CRITERE = {
  nom: "",
  prenom: "",
  date_naissance: "",
  nas: "",
  no_seq_daj: "",
  no_ref: "",
  no_dossier: "",
  no_dossier_cour: "",
  no_sed: "",
  nom_conjoint: "",
  prenom_conjoint: "",
  date_demande_de: "",
  date_demande_a: "",
  no_bur: "",
};

// Région par défaut : provient du profil utilisateur (prop defaultNoReg)

export default function RechercheModal({ onClose, onSelect, defaultNoBur = "01", defaultNoReg = "90", bureauxAutorises = [], isAdmin = false }) {
  // Employé : bureau initial = bureau courant s'il est autorisé, sinon « tous mes bureaux »
  const initialBur = isAdmin || bureauxAutorises.length === 0 || bureauxAutorises.includes(defaultNoBur)
    ? defaultNoBur : "";
  const [critere, setCritere] = useState({ ...EMPTY_CRITERE, no_bur: initialBur });
  const [results, setResults] = useState(null); // null = pas encore lancé
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const nomRef = useRef(null);

  useEffect(() => {
    if (nomRef.current) nomRef.current.focus();
  }, []);

  const update = (k, v) => setCritere(prev => ({ ...prev, [k]: v }));

  const handleSearch = async () => {
    setError("");
    setSelectedIdx(-1);
    // Validation locale : au moins 1 critère (hors no_bur)
    const hasCritere = Object.entries(critere).some(
      ([k, v]) => k !== "no_bur" && (v || "").toString().trim() !== ""
    );
    if (!hasCritere) {
      setError("Attention : veuillez entrer au moins 1 critère de recherche.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...critere, no_reg: defaultNoReg };
      const res = await axios.post(`${API}/recherche/daj`, payload);
      setResults(res.data);
      if (res.data.length === 0) {
        setError("Aucun enregistrement.");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setError(msg);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCritere({ ...EMPTY_CRITERE, no_bur: initialBur });
    setResults(null);
    setError("");
    setSelectedIdx(-1);
    if (nomRef.current) nomRef.current.focus();
  };

  const handleSelectRow = (row) => {
    if (onSelect) onSelect(row);
  };

  // Tri client par clic sur l'entête de colonne (asc → desc)
  const handleSort = (key) => {
    setSelectedIdx(-1);
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: "asc" });
  };

  const sortedResults = useMemo(() => {
    if (!results || !sort.key) return results;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...results].sort((a, b) => {
      const va = (a[sort.key] ?? "").toString().trim();
      const vb = (b[sort.key] ?? "").toString().trim();
      if (!va && !vb) return 0;
      if (!va) return 1; // valeurs vides toujours en fin
      if (!vb) return -1;
      const na = Number(va), nb = Number(vb);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return (na - nb) * dir;
      return va.localeCompare(vb, "fr", { sensitivity: "base", numeric: true }) * dir;
    });
  }, [results, sort]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading) {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleGridKey = (e) => {
    if (results && results.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIdx(i => Math.min(i + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIdx(i => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && selectedIdx >= 0) {
        e.preventDefault();
        handleSelectRow(sortedResults[selectedIdx]);
      }
    }
  };

  return (
    <div className="rech-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rech-modal" data-testid="rech-modal" onKeyDown={handleGridKey} tabIndex={-1}>
        <div className="rech-titlebar">
          <span>Recherche de demandes</span>
          <button className="rech-close" onClick={onClose} data-testid="rech-close" title="Fermer">×</button>
        </div>

        <div className="rech-body" style={{ overflowY: "hidden", display: "flex", flexDirection: "column" }}>
          {/* Critères de recherche */}
          <div className="rech-criteres">
            <div className="rech-cols">
              <div className="rech-col">
                <Field label="Nom" testid="rech-nom" inputRef={nomRef}
                       value={critere.nom} onChange={v => update("nom", v)} onKeyDown={handleKeyDown} />
                <Field label="Prénom" testid="rech-prenom"
                       value={critere.prenom} onChange={v => update("prenom", v)} onKeyDown={handleKeyDown} />
                <Field label="Date de naissance" type="date" testid="rech-date-naissance"
                       value={critere.date_naissance} onChange={v => update("date_naissance", v)} onKeyDown={handleKeyDown} />
                <Field label="N.A.S." testid="rech-nas" maxLength={11} placeholder="XXX-XXX-XXX"
                       value={critere.nas} onChange={v => update("nas", v)} onKeyDown={handleKeyDown} />
              </div>
              <div className="rech-col">
                <Field label="No dossier" testid="rech-no-dossier"
                       value={critere.no_dossier} onChange={v => update("no_dossier", v)} onKeyDown={handleKeyDown} />
                <Field label="No séquence" testid="rech-no-seq"
                       value={critere.no_seq_daj} onChange={v => update("no_seq_daj", v)} onKeyDown={handleKeyDown} />
                <Field label="No référence" testid="rech-no-ref"
                       value={critere.no_ref} onChange={v => update("no_ref", v)} onKeyDown={handleKeyDown} />
                <Field label="No dossier cour" testid="rech-no-cour" placeholder="000-00-000000-000"
                       value={critere.no_dossier_cour} onChange={v => update("no_dossier_cour", v)} onKeyDown={handleKeyDown} />
              </div>
              <div className="rech-col">
                <Field label="Nom conjoint" testid="rech-nom-conj"
                       value={critere.nom_conjoint} onChange={v => update("nom_conjoint", v)} onKeyDown={handleKeyDown} />
                <Field label="Prénom conjoint" testid="rech-prenom-conj"
                       value={critere.prenom_conjoint} onChange={v => update("prenom_conjoint", v)} onKeyDown={handleKeyDown} />
                <Field label="No SED" testid="rech-sed"
                       value={critere.no_sed} onChange={v => update("no_sed", v)} onKeyDown={handleKeyDown} />
              </div>
              <div className="rech-col">
                <Field label="Date demande (de)" type="date" testid="rech-date-de"
                       value={critere.date_demande_de} onChange={v => update("date_demande_de", v)} onKeyDown={handleKeyDown} />
                <Field label="Date demande (à)" type="date" testid="rech-date-a"
                       value={critere.date_demande_a} onChange={v => update("date_demande_a", v)} onKeyDown={handleKeyDown} />
              </div>
            </div>

            <div className="rech-actions">
              <button className="rech-btn-primary" onClick={handleSearch}
                      disabled={loading} data-testid="rech-btn-search">
                {loading ? "Recherche…" : "Rechercher"}
              </button>
              <button className="rech-btn-secondary" onClick={handleReset}
                      disabled={loading} data-testid="rech-btn-reset">
                Initialiser
              </button>
              <button className="rech-btn-secondary" onClick={onClose} data-testid="rech-btn-close">
                Fermer
              </button>
              {error && <span className="rech-error" data-testid="rech-error">{error}</span>}
              <div className="rech-actions-spacer" />
              <div className="rech-field rech-field-inline">
                <label>No bureau</label>
                {!isAdmin && bureauxAutorises.length > 0 ? (
                  <select
                    value={critere.no_bur}
                    onChange={e => update("no_bur", e.target.value)}
                    data-testid="rech-no-bur"
                    title="Limité aux bureaux autorisés de votre fiche"
                    style={{ width: 140 }}
                  >
                    <option value="">Tous mes bureaux</option>
                    {bureauxAutorises.map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={critere.no_bur}
                    onChange={e => update("no_bur", e.target.value)}
                    onKeyDown={handleKeyDown}
                    maxLength={2}
                    data-testid="rech-no-bur"
                    style={{ width: 60, textAlign: "center" }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Résultats */}
          {results !== null && results.length > 0 && (
            <div className="rech-results" style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: "0 1 auto" }}>
              <div className="rech-results-header">
                <span data-testid="rech-count">
                  <strong>{results.length}</strong> résultat{results.length > 1 ? "s" : ""}
                </span>
                <span className="rech-hint">Double-cliquez ou appuyez sur Entrée pour ouvrir</span>
              </div>
              {/* Hauteur max = 15 lignes (30px entête + 15 × 28px) — ascenseur au besoin,
                  jamais sur la fenêtre globale ; le tableau rétrécit si l'écran est petit */}
              <div className="rech-table-wrap" style={{ maxHeight: 450, minHeight: 0, flex: "0 1 auto" }}>
                <table className="rech-table">
                  <thead>
                    <tr>
                      {[
                        ["nom_requerant", "Nom Requérant"],
                        ["date_naissance", "Date Nais."],
                        ["no_bur", "No bur."],
                        ["no_seq_daj", "No séq."],
                        ["no_ref", "No réf."],
                        ["no_dossier", "No dos."],
                        ["type_dossier", "Type"],
                        ["date_demande", "Date dem."],
                        ["nas", "N.A.S."],
                        ["nature", "Nature"],
                        ["code_nature", "Code Nat."],
                        ["no_dossier_cour", "No dos. cour"],
                        ["avocat", "Avocat"],
                        ["statut", "Statut"],
                        ["no_client", "No client"],
                      ].map(([key, label]) => (
                        <th key={key}
                            className={`rech-th-sortable${sort.key === key ? " rech-th-sorted" : ""}`}
                            onClick={() => handleSort(key)}
                            title="Cliquer pour trier"
                            data-testid={`rech-th-${key}`}>
                          {label}
                          <span className="rech-sort-arrow">
                            {sort.key === key ? (sort.dir === "asc" ? "▲" : "▼") : ""}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedResults.map((r, i) => (
                      <tr key={r.pk_daj || i}
                          className={`${i === selectedIdx ? "rech-row-selected" : ""} ${r.adda152 === 1 ? "rech-row-active-image" : ""}`.trim()}
                          onClick={() => setSelectedIdx(i)}
                          onDoubleClick={() => handleSelectRow(r)}
                          data-testid={`rech-row-${i}`}>
                        <td>{r.nom_requerant || ""}</td>
                        <td>{r.date_naissance || ""}</td>
                        <td>{r.no_bur || ""}</td>
                        <td>{r.no_seq_daj || ""}</td>
                        <td>{r.no_ref || ""}</td>
                        <td>{r.no_dossier || ""}</td>
                        <td>{r.type_dossier || ""}</td>
                        <td>{r.date_demande || ""}</td>
                        <td>{r.nas || ""}</td>
                        <td>{r.nature || ""}</td>
                        <td>{r.code_nature || ""}</td>
                        <td>{r.no_dossier_cour || ""}</td>
                        <td>{r.avocat || ""}</td>
                        <td>{r.statut || ""}</td>
                        <td>{r.no_client || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, onKeyDown, type = "text", testid, maxLength, placeholder, inputRef }) {
  // Pour tout champ de type date, on utilise le composant `DateInput` qui
  // impose une année à 4 chiffres et valide le format AAAAMMJJ — évite les
  // années à 5 chiffres saisies par erreur (bug natif `<input type="date">`).
  if (type === "date") {
    return (
      <div className="rech-field">
        <label>{label}</label>
        <DateInput
          value={value}
          onChange={onChange}
          testId={testid}
          style={{ width: "100%" }}
        />
      </div>
    );
  }
  return (
    <div className="rech-field">
      <label>{label}</label>
      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        maxLength={maxLength}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}
